import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useEffect, useRef } from "react";

import {
  ApiUploadtasksList200Response,
  ApiUploadtasksListRequest,
  UploadTask,
} from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getUploadTaskSingleQuery = (uuid: string) => ({
  queryKey: ["upload-task-single", uuid],
  queryFn: () => uploadApi.apiUploadtasksRead({ uuid }),
});

export const useUploadTaskSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<UploadTask>(
      queryClient,
      ["upload-task-list"],
      uuid,
      getUploadTaskSingleQuery(uuid)
    );
};

// ─── LIST ────────────────────────────────────────────────────────────────────

const getUploadTaskListQuery = (options: ApiUploadtasksListRequest) => ({
  queryKey: ["upload-task-list", simplifyQueryParam(options)],
  queryFn: async () => {
    return await uploadApi.apiUploadtasksList(options);
  },
});

export const useUploadTaskListQuery = (options: ApiUploadtasksListRequest) => {
  return useQuery(getUploadTaskListQuery(options));
};

export const useUploadTaskListQueries = (optionsList: ApiUploadtasksListRequest[]) => {
  return useQueries({
    queries: optionsList.map(getUploadTaskListQuery),
  });
};

// ─── INVALIDATION ────────────────────────────────────────────────────────────

export const useInvalidateUploadTaskQueries = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ["upload-task-single"],
      refetchType: "all",
    });

    await queryClient.invalidateQueries({
      queryKey: getUploadTaskListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};

/**
 * Subscribes to the QueryCache and triggers a full refetch of all queries whenever
 * an upload task transitions to COMPLETED. This keeps the rest of the UI (GMW list,
 * GLD observations, etc.) in sync after the BRO backend finishes processing a task.
 *
 * Only page 1 is checked — if the response has no `previous` link it is the first page,
 * where newly submitted tasks always appear at the top.
 *
 * Two cases trigger the refetch:
 * 1. A *new* task appears at the top of the list and it is already COMPLETED — meaning
 *    a task was submitted and processed before the next poll interval fired.
 * 2. The *same* task was PROCESSING/PENDING on the previous update and is now COMPLETED.
 *
 * The hook tracks the first task per query key (i.e. per unique set of filters) so that
 * multiple concurrent upload-task-list queries (e.g. the overview table and the domain
 * stats panel) each get their own independent comparison baseline.
 *
 * IMPORTANT: call this hook exactly once at the app level (e.g. in FetchThings).
 * Calling it in multiple components would create duplicate subscribers and trigger
 * multiple refetches per completion event.
 */
export const useUploadTaskCompletionRefetch = () => {
  const queryClient = useQueryClient();
  // Keyed by serialised queryKey so each distinct upload-task-list query is tracked separately.
  const previousDataRef = useRef<Map<string, UploadTask>>(new Map());

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated") return; // Only care about query updates, not additions or removals.
      if (event.query.queryKey[0] !== "upload-task-list") return; // Only track the upload task list queries.

      const data = event.query.state.data as ApiUploadtasksList200Response | undefined;
      if (!data?.results || data.results.length === 0) return; // No tasks, nothing to check.

      const queryKeyStr = JSON.stringify(event.query.queryKey);
      const currentFirstTask = data.results[0];
      const previousFirstTask = previousDataRef.current.get(queryKeyStr);

      // If the current first task is different from the previous first task check if
      // there are filters applied (i.e. the query key has more than just "upload-task-list" in it).
      // If there are filters, don't proceed because the first upload task is most likely not the most recent one.
      if (currentFirstTask.uuid !== previousFirstTask?.uuid) {
        if (!isEqual(event.query.queryKey[1], {})) return;
      }

      if (!previousFirstTask) {
        // First fetch for this query — store baseline and wait for next update.
        previousDataRef.current.set(queryKeyStr, currentFirstTask);
        return;
      }

      const wasLoading =
        previousFirstTask.status === "PROCESSING" || previousFirstTask.status === "PENDING";
      const isCompleted = currentFirstTask.status === "COMPLETED";
      const isDifferentTask = previousFirstTask.uuid !== currentFirstTask.uuid;

      if ((isDifferentTask && isCompleted) || (wasLoading && isCompleted)) {
        // Refetch ALL queries to ensure all UI is up to date with the completed task
        queryClient.invalidateQueries({
          queryKey: [],
          refetchType: "all",
        });
      }

      previousDataRef.current.set(queryKeyStr, currentFirstTask);
    });

    return unsubscribe;
  }, [queryClient]);
};
